/**
 * Evaluation Cycle Progress Page
 *
 * Shows organization-wide progress through the evaluation cycle for HR.
 */

import { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useEvaluations } from '../evaluations/useEvaluations';
import { useEmployees } from '../employees/useEmployees';
import type { EvaluationStatus } from '../../lib/evaluationsApi';

const STATUS_ORDER: EvaluationStatus[] = ['draft', 'submitted', 'in_review', 'final'];

const STATUS_CONFIG: Record<EvaluationStatus, { label: string; color: string; description: string }> = {
  draft: {
    label: 'Concept',
    color: 'bg-yellow-400',
    description: 'Manager werkt aan de beoordeling',
  },
  submitted: {
    label: 'Ingediend',
    color: 'bg-blue-400',
    description: 'Wacht op HR review',
  },
  in_review: {
    label: 'In Review',
    color: 'bg-purple-400',
    description: 'HR beoordeelt de evaluatie',
  },
  final: {
    label: 'Definitief',
    color: 'bg-green-400',
    description: 'Afgerond en geaccordeerd',
  },
  cancelled: {
    label: 'Geannuleerd',
    color: 'bg-gray-400',
    description: 'Beoordeling geannuleerd',
  },
};

export function CycleProgressPage() {
  const navigate = useNavigate();
  const { data: evaluations, isLoading: isLoadingEvals } = useEvaluations();
  const { data: employees, isLoading: isLoadingEmps } = useEmployees();

  // Calculate cycle statistics
  const stats = useMemo(() => {
    const evals = evaluations?.data || [];
    const emps = employees?.data || [];

    // Get current year evaluations
    const currentYear = new Date().getFullYear();
    const currentYearEvals = evals.filter((e) => {
      const year = new Date(e.periodeStart).getFullYear();
      return year === currentYear && e.status !== 'cancelled';
    });

    // Status breakdown
    const statusCounts: Record<EvaluationStatus, number> = {
      draft: 0,
      submitted: 0,
      in_review: 0,
      final: 0,
      cancelled: 0,
    };
    currentYearEvals.forEach((e) => {
      statusCounts[e.status]++;
    });

    // Employees without evaluations
    const employeesWithEvals = new Set(currentYearEvals.map((e) => e.employeeId));
    const employeesWithoutEvals = emps.filter((emp) => !employeesWithEvals.has(emp.id));

    // Progress percentage
    const totalTarget = emps.length;
    const completed = statusCounts.final;
    const progressPercentage = totalTarget > 0 ? (completed / totalTarget) * 100 : 0;

    // Time-based stats (assuming evaluations should be done by year end)
    const now = new Date();
    const yearEnd = new Date(currentYear, 11, 31);
    const yearStart = new Date(currentYear, 0, 1);
    const totalDays = Math.ceil((yearEnd.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
    const daysPassed = Math.ceil((now.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, totalDays - daysPassed);
    const timeProgress = (daysPassed / totalDays) * 100;

    // Performance indicator
    const expectedProgress = (daysPassed / totalDays) * 100;
    const behindSchedule = progressPercentage < expectedProgress - 10;
    const aheadOfSchedule = progressPercentage > expectedProgress + 10;

    return {
      totalEmployees: emps.length,
      totalEvaluations: currentYearEvals.length,
      statusCounts,
      employeesWithoutEvals,
      progressPercentage,
      daysRemaining,
      timeProgress,
      behindSchedule,
      aheadOfSchedule,
      completed,
      inProgress: statusCounts.draft + statusCounts.submitted + statusCounts.in_review,
    };
  }, [evaluations?.data, employees?.data]);

  // Group evaluations by manager for detailed view
  const byManager = useMemo(() => {
    const evals = evaluations?.data || [];
    const currentYear = new Date().getFullYear();

    const grouped: Record<number, {
      managerId: number;
      managerName: string;
      total: number;
      completed: number;
      pending: number;
      evaluations: typeof evals;
    }> = {};

    evals
      .filter((e) => new Date(e.periodeStart).getFullYear() === currentYear && e.status !== 'cancelled')
      .forEach((e) => {
        const managerId = e.managerId;
        if (!grouped[managerId]) {
          grouped[managerId] = {
            managerId,
            managerName: `Manager ${managerId}`,
            total: 0,
            completed: 0,
            pending: 0,
            evaluations: [],
          };
        }
        grouped[managerId].total++;
        if (e.status === 'final') {
          grouped[managerId].completed++;
        } else {
          grouped[managerId].pending++;
        }
        grouped[managerId].evaluations.push(e);
      });

    return Object.values(grouped).sort((a, b) => b.pending - a.pending);
  }, [evaluations?.data]);

  if (isLoadingEvals || isLoadingEmps) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Voortgang laden...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="cycle-progress-page" className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Terug naar dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Beoordelingscyclus Voortgang</h1>
          <p className="mt-1 text-sm text-gray-500">
            Volg de voortgang van alle beoordelingen in de huidige cyclus.
          </p>
        </div>

        {/* Main Progress Card */}
        <div className="mb-8 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Cyclus {new Date().getFullYear()}</h3>
              <p className="text-sm text-gray-500">
                {stats.completed} van {stats.totalEmployees} medewerkers afgerond
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">{stats.progressPercentage.toFixed(0)}%</p>
              <p className={`text-sm ${
                stats.behindSchedule ? 'text-red-600' : stats.aheadOfSchedule ? 'text-green-600' : 'text-gray-500'
              }`}>
                {stats.behindSchedule ? '⚠️ Achter op schema' : stats.aheadOfSchedule ? '✓ Voor op schema' : 'Op schema'}
              </p>
            </div>
          </div>

          {/* Dual Progress Bars */}
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Beoordelingen voortgang</span>
                <span>{stats.progressPercentage.toFixed(0)}%</span>
              </div>
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${stats.progressPercentage}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Tijdsvoortgang (jaar)</span>
                <span>{stats.daysRemaining} dagen resterend</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gray-300 rounded-full"
                  style={{ width: `${stats.timeProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Status Pipeline */}
        <div className="mb-8 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status Pipeline</h3>
          <div className="flex items-center justify-between gap-4">
            {STATUS_ORDER.map((status, index) => {
              const count = stats.statusCounts[status];
              const config = STATUS_CONFIG[status];

              return (
                <div key={status} className="flex-1">
                  <div className="flex items-center">
                    {/* Status Box */}
                    <div className="flex-1">
                      <div className={`${config.color} rounded-lg p-4 text-white text-center`}>
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-sm opacity-90">{config.label}</p>
                      </div>
                      <p className="text-xs text-gray-500 text-center mt-2">{config.description}</p>
                    </div>
                    {/* Arrow */}
                    {index < STATUS_ORDER.length - 1 && (
                      <div className="flex-shrink-0 px-2">
                        <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Employees Without Evaluations */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Nog Niet Gestart</h3>
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                {stats.employeesWithoutEvals.length} medewerkers
              </span>
            </div>
            <div className="p-4 max-h-64 overflow-y-auto">
              {stats.employeesWithoutEvals.length > 0 ? (
                <ul className="space-y-2">
                  {stats.employeesWithoutEvals.slice(0, 10).map((emp) => (
                    <li key={emp.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{emp.name}</p>
                        <p className="text-xs text-gray-500">{emp.email}</p>
                      </div>
                      <span className="text-xs text-gray-400">Geen beoordeling</span>
                    </li>
                  ))}
                  {stats.employeesWithoutEvals.length > 10 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      + {stats.employeesWithoutEvals.length - 10} meer
                    </p>
                  )}
                </ul>
              ) : (
                <p className="text-sm text-green-600 text-center py-4">
                  ✓ Alle medewerkers hebben een beoordeling
                </p>
              )}
            </div>
          </div>

          {/* Manager Progress */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Voortgang per Manager</h3>
            </div>
            <div className="p-4 max-h-64 overflow-y-auto">
              {byManager.length > 0 ? (
                <ul className="space-y-3">
                  {byManager.slice(0, 8).map((manager) => {
                    const progress = manager.total > 0 ? (manager.completed / manager.total) * 100 : 0;
                    return (
                      <li key={manager.managerId} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">{manager.managerName}</span>
                          <span className="text-xs text-gray-500">
                            {manager.completed}/{manager.total} ({progress.toFixed(0)}%)
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              progress === 100 ? 'bg-green-500' : progress > 50 ? 'bg-blue-500' : 'bg-yellow-500'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">Geen beoordelingen gestart</p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4">Snelle Acties</h3>
          <div className="flex flex-wrap gap-4">
            {stats.statusCounts.submitted > 0 && (
              <Link
                to="/hr?status=submitted"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-lg text-blue-800 hover:bg-blue-100"
              >
                <span className="flex items-center justify-center w-6 h-6 bg-blue-200 rounded-full text-sm font-bold">
                  {stats.statusCounts.submitted}
                </span>
                <span>Ingediende beoordelingen reviewen</span>
              </Link>
            )}
            {stats.statusCounts.in_review > 0 && (
              <Link
                to="/hr?status=in_review"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 rounded-lg text-purple-800 hover:bg-purple-100"
              >
                <span className="flex items-center justify-center w-6 h-6 bg-purple-200 rounded-full text-sm font-bold">
                  {stats.statusCounts.in_review}
                </span>
                <span>Beoordelingen accorderen</span>
              </Link>
            )}
            <Link
              to="/hr"
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              <span>Naar HR Dashboard →</span>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
